import React from 'react';
import { Edit, Inbox, Send, FileText, Archive, Trash2, Calendar, FolderPlus, MoreVertical, Folder, Star, ChevronLeft, ChevronRight, ShieldAlert, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SidebarSection } from './SidebarSection';
import { SidebarItem } from './SidebarItem';
import ContextMenu from '../ContextMenu';
import { apiFetch } from '../../services/api';


const Sidebar = ({
    activeFolder,
    setActiveFolder,
    isComposeOpen,
    setIsComposeOpen,
    isSidebarOpen,
    setIsSidebarOpen,
    isCollapsed,
    setIsCollapsed,
    unreadCount,
    inboxUnreadCount,
    storageUsage,
    user
}) => {
    const [customFolders, setCustomFolders] = React.useState(() => {
        if (!user) return [];
        const saved = localStorage.getItem(`xemail_custom_folders_${user.email}`);
        return saved ? JSON.parse(saved) : [];
    });
    const [isCreatingFolder, setIsCreatingFolder] = React.useState(false);
    const [newFolderName, setNewFolderName] = React.useState('');
    const [contextMenu, setContextMenu] = React.useState(null);

    React.useEffect(() => {
        if (user) {
            localStorage.setItem(`xemail_custom_folders_${user.email}`, JSON.stringify(customFolders));
        }
    }, [customFolders, user?.email]);

    const fetchFolders = React.useCallback(async () => {
        if (!user) return;
        try {
            const response = await apiFetch('/mails/m-boxes');

            if (response.ok) {
                const dbFolders = await response.json();
                const systemFolders = ['INBOX', 'Sent', 'Archive', 'Trash', 'Drafts', 'Spam', 'Calendar', 'Inbox', 'sent', 'archive', 'trash'];

                // Keep Shared folders and other non-system folders
                const nonSystem = dbFolders.filter(f => !systemFolders.some(s => s.toLowerCase() === f.toLowerCase()));

                setCustomFolders(prev => {
                    // Create a Map with normalized keys to handle case sensitivity and merging
                    const folderMap = new Map();

                    // Add existing local folders first
                    prev.forEach(f => folderMap.set(f.toLowerCase(), f));

                    // Add/Override with server folders
                    nonSystem.forEach(f => folderMap.set(f.toLowerCase(), f));

                    return Array.from(folderMap.values());
                });
            }
        } catch (err) {
            console.error("Failed to fetch folders:", err);
        }
    }, [user?.email]);

    React.useEffect(() => {
        fetchFolders();
        const interval = setInterval(fetchFolders, 30000);
        return () => clearInterval(interval);
    }, [fetchFolders]);

    const handleFolderClick = (folder) => {
        setActiveFolder(folder);
        setIsComposeOpen(false);
        setIsSidebarOpen(false);
    };

    const handleContextMenu = (e, folder) => {
        e.preventDefault();
        setContextMenu({
            x: e.clientX,
            y: e.clientY,
            folder: folder
        });
    };

    const handleCreateFolder = async () => {
        const name = newFolderName.trim();
        if (name) {
            // Optimistic update
            if (!customFolders.includes(name)) {
                setCustomFolders(prev => [...prev, name]);
            }

            const originalFolders = [...customFolders];
            setNewFolderName('');
            setIsCreatingFolder(false);

            try {
                const response = await apiFetch('/mails/create-folder', {
                    method: 'POST',
                    body: JSON.stringify({ folderName: name })
                });

                if (!response.ok) {
                    console.error("Failed to create folder on server");
                    setCustomFolders(originalFolders); // Revert on failure
                }
            } catch (err) {
                console.error("Create folder error:", err);
                setCustomFolders(originalFolders); // Revert on failure
            }
        }
    };

    const deleteFolder = async (folderName) => {
        if (!window.confirm(`Delete folder "${folderName}"? All emails in it will be moved to Trash and rules will be removed.`)) return;

        try {
            const response = await apiFetch('/mails/delete-folder', {
                method: 'POST',
                body: JSON.stringify({ folderName })
            });

            if (response.ok) {
                setCustomFolders(customFolders.filter(f => f !== folderName));
                if (activeFolder === folderName) {
                    setActiveFolder('INBOX');
                }
            } else {
                alert('Failed to delete folder from server');
            }
        } catch (err) {
            console.error("Delete folder error:", err);
            alert('Connection error while deleting folder');
        }
    };

    const handleComposeClick = () => {
        setIsComposeOpen(true);
        setIsSidebarOpen(false);
    };

    const toggleCollapse = () => {
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        localStorage.setItem('xemail_sidebar_collapsed', newState.toString());
    };

    return (
        <motion.div style={{ display: 'contents' }}>
            <aside
                className="sidebar-container"
                style={{
                    width: isCollapsed ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)',
                    height: '100%',
                    background: 'var(--bg-main)',
                    borderRight: '1px solid var(--border-glass)',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: 100,
                    padding: '16px 0',
                    position: window.innerWidth <= 768 ? 'fixed' : 'relative',
                    left: window.innerWidth <= 768 ? (isSidebarOpen ? 0 : '-100%') : 0
                }}
            >
                {/* User Context & Search (Desktop) */}

                <div style={{ padding: '24px 16px 8px' }}>
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setIsComposeOpen(true)}
                        className="btn-primary"
                        style={{
                            width: 'fit-content',
                            padding: isCollapsed ? '10px' : '8px 20px',
                            justifyContent: 'center',
                            borderRadius: '12px',
                            background: 'var(--primary)',
                            color: '#ffffff',
                            fontWeight: '600',
                            fontSize: '0.9rem'
                        }}
                    >
                        <Edit size={18} strokeWidth={2.5} />
                        {!isCollapsed && <span style={{ marginLeft: '8px' }}>New Mail</span>}
                    </motion.button>

                    {!isCollapsed && (
                        <div style={{
                            marginTop: '24px',
                            paddingLeft: '4px',
                            fontSize: '0.65rem',
                            fontWeight: '700',
                            color: 'var(--text-dim)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>
                            {user?.email}
                        </div>
                    )}
                </div>

                <div className="scrollbar-hide" style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '0 12px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div onContextMenu={(e) => handleContextMenu(e, 'INBOX')}>
                            <SidebarItem
                                icon={Inbox}
                                label="Inbox"
                                isCollapsed={isCollapsed}
                                active={activeFolder === 'INBOX' && !isComposeOpen}
                                onClick={() => handleFolderClick('INBOX')}
                                count={inboxUnreadCount > 0 ? inboxUnreadCount : null}
                            />
                        </div>
                        <SidebarItem
                            icon={Send}
                            label="Sent"
                            isCollapsed={isCollapsed}
                            active={activeFolder === 'Sent' && !isComposeOpen}
                            onClick={() => handleFolderClick('Sent')}
                        />
                        <SidebarItem
                            icon={Star}
                            label="Important"
                            isCollapsed={isCollapsed}
                            active={activeFolder === 'Important' && !isComposeOpen}
                            onClick={() => handleFolderClick('Important')}
                        />
                        <SidebarItem
                            icon={FileText}
                            label="Drafts"
                            isCollapsed={isCollapsed}
                            active={activeFolder === 'Drafts' && !isComposeOpen}
                            onClick={() => handleFolderClick('Drafts')}
                        />
                        <SidebarItem
                            icon={Archive}
                            label="Archive"
                            isCollapsed={isCollapsed}
                            active={activeFolder === 'Archive' && !isComposeOpen}
                            onClick={() => handleFolderClick('Archive')}
                        />
                        <SidebarItem
                            icon={Trash2}
                            label="Trash"
                            isCollapsed={isCollapsed}
                            active={activeFolder === 'Trash' && !isComposeOpen}
                            onClick={() => handleFolderClick('Trash')}
                        />
                        <SidebarItem
                            icon={ShieldAlert}
                            label="Spam"
                            isCollapsed={isCollapsed}
                            active={activeFolder === 'Spam' && !isComposeOpen}
                            onClick={() => handleFolderClick('Spam')}
                        />

                        {/* Custom Folders integrated into Primary */}
                        <AnimatePresence>
                            {isCreatingFolder && !isCollapsed && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    style={{ padding: '4px 16px', paddingLeft: '32px' }}
                                >
                                    <input
                                        autoFocus
                                        value={newFolderName}
                                        onChange={(e) => setNewFolderName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleCreateFolder();
                                            if (e.key === 'Escape') setIsCreatingFolder(false);
                                        }}
                                        onBlur={handleCreateFolder}
                                        placeholder="Folder name"
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            borderRadius: '8px',
                                            border: '1px solid var(--primary)',
                                            background: 'var(--bg-secondary)',
                                            color: 'var(--text-main)',
                                            fontSize: '0.85rem',
                                            outline: 'none'
                                        }}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                        {customFolders.filter(f => !f.startsWith('Shared/')).map(folder => (
                            <div key={folder} onContextMenu={(e) => handleContextMenu(e, folder)}>
                                <SidebarItem
                                    icon={Folder}
                                    label={folder}
                                    isCollapsed={isCollapsed}
                                    active={activeFolder === folder && !isComposeOpen}
                                    onClick={() => handleFolderClick(folder)}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Shared Mailboxes at the absolute bottom of the list */}
                    {customFolders.some(f => f.startsWith('Shared/')) && (
                        <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--border-glass)' }}>
                            <SidebarSection title="Shared Accounts" isCollapsed={isCollapsed} onRefresh={fetchFolders}>
                                {customFolders.filter(f => f.startsWith('Shared/')).map(folder => {
                                    const email = folder.substring(7);
                                    const accountName = email.split('@')[0];
                                    const isExpanded = activeFolder.startsWith(folder);

                                    return (
                                        <div key={folder} onContextMenu={(e) => handleContextMenu(e, folder)}>
                                            <SidebarItem
                                                icon={Users}
                                                label={accountName}
                                                isCollapsed={isCollapsed}
                                                active={activeFolder === folder && !isComposeOpen}
                                                onClick={() => handleFolderClick(folder)}
                                                title={email}
                                            />
                                        </div>
                                    );
                                })}
                            </SidebarSection>
                        </div>
                    )}
                </div>

                <div style={{ padding: '20px 16px 16px', borderTop: '1px solid var(--border-glass)', minHeight: '60px' }}>
                    {!isCollapsed && storageUsage ? (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.65rem', fontWeight: '700', color: storageUsage.warning ? '#ef4444' : 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    {storageUsage.warning && <ShieldAlert size={10} />}
                                    Storage
                                </span>
                                <span>{(storageUsage.usage / (1024 * 1024)).toFixed(1)} MB / {(storageUsage.limit / (1024 * 1024)).toFixed(0)} MB</span>
                            </div>
                            <div style={{ height: '6px', background: 'var(--bg-glass)', borderRadius: '3px', overflow: 'hidden' }}>
                                <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${Math.min(100, (storageUsage.usage / (storageUsage.limit || 1)) * 100)}%` }}
                                    style={{
                                        height: '100%',
                                        background: storageUsage.warning ? '#ef4444' : 'var(--primary)',
                                        borderRadius: '3px'
                                    }}
                                />
                            </div>
                        </>
                    ) : (
                        storageUsage && (
                            <div style={{
                                height: '6px',
                                width: '100%',
                                background: 'var(--bg-glass)',
                                borderRadius: '3px',
                                overflow: 'hidden',
                                border: storageUsage.warning ? '1px solid #ef4444' : 'none'
                            }}>
                                <div style={{
                                    height: '100%',
                                    width: `${Math.min(100, (storageUsage.usage / (storageUsage.limit || 1)) * 100)}%`,
                                    background: storageUsage.warning ? '#ef4444' : 'var(--primary)'
                                }} />
                            </div>
                        )
                    )}
                </div>

                {/* Collapse Toggle */}
                <div style={{ marginTop: '16px', display: 'flex', justifyContent: isCollapsed ? 'center' : 'flex-end' }}>
                    <button
                        onClick={toggleCollapse}
                        className="btn-ghost"
                        style={{
                            padding: '8px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'var(--bg-secondary)',
                            border: '1px solid var(--border-glass)'
                        }}
                    >
                        {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                    </button>
                </div>
            </aside>

            {isSidebarOpen && window.innerWidth <= 768 && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => setIsSidebarOpen(false)}
                    style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', zIndex: 104 }}
                />
            )}

            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    options={[
                        {
                            label: 'New Folder...',
                            icon: FolderPlus,
                            onClick: () => setIsCreatingFolder(true)
                        },
                        {
                            label: 'Mark All as Read',
                            icon: Inbox,
                            onClick: () => console.log('Mark all as read')
                        },
                        ...(customFolders.includes(contextMenu.folder) ? [{
                            label: 'Delete Folder',
                            icon: Trash2,
                            onClick: () => deleteFolder(contextMenu.folder),
                            danger: true
                        }] : [])
                    ]}
                    onClose={() => setContextMenu(null)}
                />
            )}
        </motion.div>
    );
};

export default Sidebar;
